import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { HorizontalHeaders } from "../pages/components/FuturesTable/utils/header.utils";
import useCellTab from "../hooks/useCellTab";
import { ForecastContext } from "./ForecastContext";
import { ShipmentHeaderContext } from "./ShipmentHeaderContext";
import { useLocation } from "react-router-dom";
import { autoUpdateForecast } from "../constants/utils";
import { SeasonToMonthContext } from "./SeasonToMonthContext";
import {DueDateContext} from "./DueDateContext";

export const FuturesContext = createContext<{
  futuresData: Array<Array<number>>;
  setFuturesData: (val: Array<Array<number>>) => void;
  futuresTotals: number[];
  setFuturesTotals: (val: number[]) => void;
  selectedCell: { row: number; column: number };
  setNextCell: (row: number, column: number) => () => void;
}>({
  futuresData: [[0]],
  setFuturesData: (val) => val,
  futuresTotals: [0],
  setFuturesTotals: (val) => val,
  selectedCell: { row: -1, column: -1 },
  setNextCell: (row, column) => () => row + column,
});

export const createFuturesDefaultData = (
  verticalMonths: string[],
  horizontalHeaders: HorizontalHeaders,
) => new Array(24).fill(0).map((_) => horizontalHeaders.MONTHS.map((_) => 0));
export const createFuturesDefaultTotals = (
  horizontalHeaders: HorizontalHeaders,
) => horizontalHeaders.MONTHS.map((_) => 0);

export const FuturesProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const { horizontalHeaders, verticalMonths, monthsInFirstTwoSeasons } =
    useContext(ShipmentHeaderContext);
  const { originalForecast, status } = useContext(ForecastContext);
  const { cupData } = useContext(SeasonToMonthContext);
  const futuresDefaultData = createFuturesDefaultData(
    verticalMonths,
    horizontalHeaders,
  );
  const [futuresData, setFuturesData] =
    useState<Array<Array<number>>>(futuresDefaultData);
  const futuresDefaultTotals = createFuturesDefaultTotals(horizontalHeaders);
  const [futuresTotals, setFuturesTotals] =
    useState<Array<number>>(futuresDefaultTotals);
  const { selectedCell, setNextCell } = useCellTab(
    horizontalHeaders.MONTHS.length,
    verticalMonths.length,
  );
  const { windowStatus } = useContext(DueDateContext);
  const areLongRangeCalculationsAllowed = (
      cacheStatus: string
  ) => windowStatus === "OPEN" && cacheStatus === "base";

  useEffect(() => {
    if (originalForecast.futures && originalForecast.futures[0].length > 1) {
      const newFuturesData = originalForecast.futures.map((forecast, row) => {
        const forecasts = forecast
          .map((month) => month)
          .slice(0, horizontalHeaders.MONTHS.length + 1);
        if (!pathname.includes("3pw")) {
          const totals = forecasts.reduce((acc, val) => acc + val, 0);
          return forecasts.concat([totals]);
        }
        const totals = forecasts
          .reduce((acc, val) => acc + val, 0);
        const futurePG2NTotal = originalForecast.futures_pg2n_totals
          ? originalForecast.futures_pg2n_totals[row][0]
          : 0;
        const variance =
          row > monthsInFirstTwoSeasons + 6 ? totals - futurePG2NTotal : 0;
        return forecasts.concat([totals, futurePG2NTotal, variance]);
      });

      if (pathname.includes("3pw") && !pathname.includes("tb")) {
        const futures_LR_totals = originalForecast.history_LR_totals;
        const ccdMonthTotal = futures_LR_totals ? futures_LR_totals[0] : 0;
        if (areLongRangeCalculationsAllowed(status)) {
          const totals = autoUpdateForecast(
            newFuturesData,
            monthsInFirstTwoSeasons,
            originalForecast.futures_pg2n_totals,
            cupData[cupData.length - 1],
            ccdMonthTotal,
          );
          setFuturesTotals(totals);
          setFuturesData(newFuturesData);
        }
        else {
          //Long Range calculation updates to Futures table (from hist shp percentages edits) are not allowed, but
          // we still allow edits to the Futures table directly, so we update
          // any new direct Futures table edits and recalculate totals here.
          const totals = newFuturesData.reduce((acc, curr) => {
            if (acc.length === 0) {
              return curr.slice(0, curr.length - 3).map((val) => val);
            }

            return acc.map((val, i) => val + curr[i]);
          }, [] as number[]);
          setFuturesTotals(totals);
          setFuturesData(newFuturesData);
        }
      } else {
        const newFuturesTotals = newFuturesData.reduce((acc, row) => {
          const amountToSlice = pathname.includes("3pw") ? 3 : 1;
          if (acc.length === 0) return row.slice(0, row.length - amountToSlice);
          return acc.map((val, i) => val + row[i]);
        }, []);
        setFuturesData(newFuturesData);
        setFuturesTotals(newFuturesTotals);
      }
    }
  }, [originalForecast, cupData]);
  return (
    <FuturesContext.Provider
      value={{
        futuresData,
        setFuturesData,
        futuresTotals,
        setFuturesTotals,
        selectedCell,
        setNextCell,
      }}
    >
      {children}
    </FuturesContext.Provider>
  );
};
